import { BaseService } from './BaseService';
import { Cause, Transaction, Report } from '../types/accounting';
import { DatabaseError } from '../utils/errors';
import { Client } from '../types/client';
import { Pool } from 'pg';
import mongoose from 'mongoose';
import { QueryResult } from 'pg';

// Define the FiscalSponsorStatus enum
export enum FiscalSponsorStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended'
}

// Define the FiscalSponsor interface
export interface FiscalSponsor {
  id: string;
  name: string;
  ein: string;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactEmail: string;
  contactPhone: string;
  status: FiscalSponsorStatus;
  feeStructure: Record<string, any>;
  website?: string;
  mission?: string;
  foundedDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Add a MongoDB client type that matches your actual document structure
interface MongoDBClient {
  _id: {
    $oid: string;
  };
  id: string;
  name: string;
  ein: string;
  type: string;
  status: string;
  adminUsers: Array<{ $oid: string }>;
  sponsoredOrganizations: any[];
  services: any[];
  metadata: {
    website: string;
    taxExemptStatus: string;
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
  };
  settings: {
    allowsMultipleAdmins: boolean;
    feesStructure: Record<string, any>;
  };
  form990Data?: {
    officers: Array<any>;
    organizationInfo: Record<string, any>;
    contact: Record<string, any>;
    questionnaire: Record<string, any>;
  };
  createdAt: { $date: { $numberLong: string } };
  updatedAt: { $date: { $numberLong: string } };
  __v: { $numberInt: string };
}

// Define interfaces for PostgreSQL types to avoid direct imports
interface PgClientType {
  query: (text: string, params?: any[]) => Promise<any>;
  release: () => void;
}

interface PgPoolType {
  connect: () => Promise<PgClientType>;
  query: (text: string, params?: any[]) => Promise<any>;
}

// Create placeholder for PostgreSQL imports
let PgPool: { new(config: any): PgPoolType };

// Use dynamic import for PostgreSQL in Node.js environment
if (typeof window === 'undefined') {
  // Server-side code
  try {
    const pg = require('pg');
    PgPool = pg.Pool;
  } catch (e) {
    console.error('Failed to load pg:', e);
    // Provide fallback implementation
    PgPool = class {
      constructor(config: any) {}
      async connect() { 
        return { 
          query: async () => ({ rows: [] }), 
          release: () => {} 
        }; 
      }
      async query() { return { rows: [] }; }
    } as any;
  }
} else {
  // Client-side code - provide mock implementation
  PgPool = class {
    constructor(config: any) {}
    async connect() { 
      return { 
        query: async () => ({ rows: [] }), 
        release: () => {} 
      }; 
    }
    async query() { return { rows: [] }; }
  } as any;
}

export class FiscalSponsorService extends BaseService {
  protected pool: any;

  constructor() {
    super();
    
    // Initialize the pool
    this.pool = new PgPool({
      host: process.env.DB_HOST || 'localhost',
      port: parseInt(process.env.DB_PORT || '5432'),
      database: process.env.DB_NAME || 'sponsfi',
      user: process.env.DB_USER || 'postgres',
      password: process.env.DB_PASSWORD || 'postgres'
    });
  }

  async list(filters?: Record<string, any>): Promise<FiscalSponsor[]> {
    try {
      const query = `
        SELECT * FROM fiscal_sponsors
        WHERE ($1::jsonb IS NULL OR data @> $1::jsonb)
        ORDER BY created_at DESC
      `;
      const result = await this.pool.query(query, [filters]);
      return result.rows.map(this.mapRowToFiscalSponsor);
    } catch (error) {
      throw new DatabaseError('Failed to list fiscal sponsors', error);
    }
  }

  async get(id: string): Promise<FiscalSponsor> {
    try {
      const result = await this.pool.query(
        'SELECT * FROM fiscal_sponsors WHERE id = $1',
        [id]
      );
      return this.mapRowToFiscalSponsor(result.rows[0]);
    } catch (error) {
      throw new DatabaseError('Failed to get fiscal sponsor', error);
    }
  }

  async create(data: {
    name: string;
    ein: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
    contactEmail: string;
    contactPhone: string;
    status?: FiscalSponsorStatus;
    feeStructure?: Record<string, any>;
    website?: string;
    mission?: string;
    foundedDate?: Date;
  }): Promise<FiscalSponsor> {
    try {
      const result = await this.pool.query(
        `INSERT INTO fiscal_sponsors (
          name, 
          ein, 
          address, 
          contact_email, 
          contact_phone, 
          status, 
          fee_structure, 
          website, 
          mission, 
          founded_date
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)
        RETURNING *`,
        [
          data.name,
          data.ein,
          data.address,
          data.contactEmail,
          data.contactPhone,
          data.status || FiscalSponsorStatus.PENDING,
          data.feeStructure || {},
          data.website,
          data.mission,
          data.foundedDate
        ]
      );
      return this.mapRowToFiscalSponsor(result.rows[0]);
    } catch (error) {
      throw new DatabaseError('Failed to create fiscal sponsor', error);
    }
  }

  async update(id: string, data: Partial<{
    name: string;
    ein: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
    contactEmail: string;
    contactPhone: string;
    status: FiscalSponsorStatus;
    feeStructure: Record<string, any>;
    website?: string;
    mission?: string;
    foundedDate?: Date;
  }>): Promise<FiscalSponsor> {
    try {
      const result = await this.pool.query(
        `UPDATE fiscal_sponsors SET
          name = $2,
          ein = $3,
          address = $4,
          contact_email = $5,
          contact_phone = $6,
          status = $7,
          fee_structure = $8,
          website = $9,
          mission = $10,
          founded_date = $11
         WHERE id = $1
         RETURNING *`,
        [
          id,
          data.name,
          data.ein,
          data.address,
          data.contactEmail,
          data.contactPhone,
          data.status,
          data.feeStructure,
          data.website,
          data.mission,
          data.foundedDate
        ]
      );
      return this.mapRowToFiscalSponsor(result.rows[0]);
    } catch (error) {
      throw new DatabaseError('Failed to update fiscal sponsor', error);
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await this.pool.query(
        'DELETE FROM fiscal_sponsors WHERE id = $1',
        [id]
      );
    } catch (error) {
      throw new DatabaseError('Failed to delete fiscal sponsor', error);
    }
  }

  async listCauses(fiscalSponsorId: string): Promise<Cause[]> {
    try {
      const result = await this.pool.query(
        'SELECT * FROM causes WHERE fiscal_sponsor_id = $1 ORDER BY created_at DESC',
        [fiscalSponsorId]
      );
      return result.rows;
    } catch (error) {
      throw new DatabaseError('Failed to list causes', error);
    }
  }

  async listTransactions(fiscalSponsorId: string): Promise<Transaction[]> {
    try {
      const result = await this.pool.query(
        'SELECT * FROM transactions WHERE fiscal_sponsor_id = $1 ORDER BY date DESC',
        [fiscalSponsorId]
      );
      return result.rows;
    } catch (error) {
      throw new DatabaseError('Failed to list transactions', error);
    }
  }

  async listReports(fiscalSponsorId: string): Promise<Report[]> {
    try {
      const result = await this.pool.query(
        'SELECT * FROM reports WHERE fiscal_sponsor_id = $1 ORDER BY created_at DESC',
        [fiscalSponsorId]
      );
      return result.rows;
    } catch (error) {
      throw new DatabaseError('Failed to list reports', error);
    }
  }

  async getCurrentFiscalSponsor(userId: string) {
    try {
      // First get the fiscal sponsor from MongoDB
      const user = await mongoose.model('User').findById(userId).populate('fiscalSponsor');
      
      if (!user?.fiscalSponsor) {
        console.log('No fiscal sponsor found for user:', userId);
        return null;
      }

      // Get additional financial data from PostgreSQL if needed
      const query = `
        SELECT fs.* 
        FROM sponsfi.fiscal_sponsors fs
        WHERE fs.id = $1 
        AND fs.status = 'active'
      `;

      const result = await this.pool.query(query, [user.fiscalSponsor._id.toString()]);
      
      // Merge MongoDB and PostgreSQL data
      return {
        ...user.fiscalSponsor.toObject(),
        financialData: result.rows[0] || {}
      };
    } catch (error) {
      console.error('Error getting fiscal sponsor:', error);
      throw error;
    }
  }

  // Update the getDashboardData method to handle both Client and MongoDB client formats
  async getDashboardData(client: Client | MongoDBClient) {
    try {
      const query = `
        // ... existing query ...
      `;

      // Handle both Client and MongoDB client formats
      let clientId: string;
      
      if ('_id' in client) {
        // MongoDB format
        clientId = client.id;
      } else {
        // Regular Client format
        clientId = client.id;
      }

      const result = await this.pool.query(query, [clientId]);
      
      // Merge MongoDB and PostgreSQL data
      return {
        // ... existing return data ...
      };
    } catch (error) {
      // ... existing error handling ...
    }
  }

  // Add a method to convert MongoDB document to Client format if needed
  convertMongoDBToClient(mongoClient: MongoDBClient): Client {
    return {
      id: mongoClient.id,
      name: mongoClient.name,
      ein: mongoClient.ein,
      type: mongoClient.type as 'FISCAL_SPONSOR' | 'NONPROFIT',
      status: mongoClient.status as 'active' | 'inactive' | 'pending',
      phone: '', // Extract from MongoDB document if available
      email: '', // Extract from MongoDB document if available
      website: mongoClient.metadata?.website,
      address: {
        street1: mongoClient.metadata?.address?.street || '',
        city: mongoClient.metadata?.address?.city || '',
        state: mongoClient.metadata?.address?.state || '',
        zipCode: mongoClient.metadata?.address?.zip || '',
        country: mongoClient.metadata?.address?.country
      },
      metadata: {
        createdAt: new Date(parseInt(mongoClient.createdAt.$date.$numberLong)),
        updatedAt: new Date(parseInt(mongoClient.updatedAt.$date.$numberLong)),
        createdBy: '',
        updatedBy: ''
      }
    };
  }

  async createFiscalSponsor(fiscalSponsor: Partial<FiscalSponsor>): Promise<FiscalSponsor> {
    return await this.withTransaction(async (client) => {
      const query = `
        INSERT INTO sponsfi.fiscal_sponsors (
          name, ein, address, contact_email, contact_phone, 
          status, fee_structure, website, mission, founded_date
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)
        RETURNING *
      `;

      const result = await this.query(query, [
        fiscalSponsor.name,
        fiscalSponsor.ein,
        fiscalSponsor.address,
        fiscalSponsor.contactEmail,
        fiscalSponsor.contactPhone,
        fiscalSponsor.status || FiscalSponsorStatus.PENDING,
        fiscalSponsor.feeStructure || {},
        fiscalSponsor.website,
        fiscalSponsor.mission,
        fiscalSponsor.foundedDate
      ], client);

      return this.mapRowToFiscalSponsor(result.rows[0]);
    });
  }

  async getFiscalSponsorById(id: string): Promise<FiscalSponsor | null> {
    const query = `
      SELECT * FROM sponsfi.fiscal_sponsors
      WHERE id = $1
    `;

    const result = await this.query(query, [id]);
    
    if (result.rows.length === 0) {
      return null;
    }

    return this.mapRowToFiscalSponsor(result.rows[0]);
  }

  async updateFiscalSponsor(id: string, updates: Partial<FiscalSponsor>): Promise<FiscalSponsor> {
    return await this.withTransaction(async (client) => {
      // Build the update query dynamically based on provided fields
      const setClause = Object.keys(updates)
        .filter(key => updates[key as keyof Partial<FiscalSponsor>] !== undefined)
        .map((key, index) => {
          // Convert camelCase to snake_case for database columns
          const column = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          return `${column} = $${index + 2}`;
        })
        .join(', ');

      if (!setClause) {
        throw new Error('No valid fields to update');
      }

      const query = `
        UPDATE sponsfi.fiscal_sponsors
        SET ${setClause}
        WHERE id = $1
        RETURNING *
      `;

      const values = [
        id,
        ...Object.keys(updates)
          .filter(key => updates[key as keyof Partial<FiscalSponsor>] !== undefined)
          .map(key => updates[key as keyof Partial<FiscalSponsor>])
      ];

      const result = await this.query(query, values, client);
      
      if (result.rows.length === 0) {
        throw new Error(`Fiscal sponsor with ID ${id} not found`);
      }

      return this.mapRowToFiscalSponsor(result.rows[0]);
    });
  }

  async getAllFiscalSponsors(): Promise<FiscalSponsor[]> {
    const query = `
      SELECT * FROM sponsfi.fiscal_sponsors
      ORDER BY name ASC
    `;

    const result = await this.query(query, []);
    
    return result.rows.map(row => this.mapRowToFiscalSponsor(row));
  }

  private mapRowToFiscalSponsor(row: any): FiscalSponsor {
    return {
      id: row.id,
      name: row.name,
      ein: row.ein,
      address: row.address || {
        street1: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      contactEmail: row.contact_email || '',
      contactPhone: row.contact_phone || '',
      status: (row.status || 'pending') as FiscalSponsorStatus,
      feeStructure: row.fee_structure || {},
      website: row.website,
      mission: row.mission,
      foundedDate: row.founded_date ? new Date(row.founded_date) : undefined,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at)
    };
  }

  protected async query(
    queryText: string,
    values: any[],
    client?: PgClientType
  ): Promise<QueryResult> {
    if (client) {
      return await client.query(queryText, values);
    }
    
    // Add null check for pool
    if (!this.pool) {
      throw new Error('Database pool not initialized');
    }
    
    return await this.pool.query(queryText, values);
  }
} 